import { Separator } from "@/components/ui/separator";
import { Dot, EllipsisVertical, User, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import FormInputField from "@/components/lib/from-input-field";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { toast } from "react-hot-toast";
import useAuth from "@/hooks/useAuth";
import { createBookingTable, createRoom, createTable, updateBookingTable, updateRoom, updateTable } from "@/api/table-floor/tableFloor";
import { useSelector, useDispatch } from "react-redux";
import { closeAddNewBookingTableModal, fetchAllTables } from "@/redux/slice/table-slice/table-slice";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import CustomerMultiSelect from "@/components/loyalty/CustomerMultiSelect";
import { getAllCustomerApi } from "@/api/customer/customer";
import { getAllStaff } from "@/api/staff";
import { formatTime } from "@/lib/bookingUtils";
import { fetchAllStaff } from "@/redux/slice/staff-management/staff-attendance";
import { format } from "date-fns";

const addNewFloorCardSchema = z.object({
  customer_phone: z.string().min(5, "Phone number is required"),
  customer_name: z.string().min(1, "Name is required"),
  customer_email: z.string().min(1, "Email is required"),
  reservation_type: z.string().min(1, "Reservation type is required"),
  staff_id: z.coerce.number().optional(),
  reservation_date: z.preprocess((val) => {
    if (!val) return undefined;
    if (typeof val === "string") return val.split("T")[0];
    if (val instanceof Date) return format(val, "yyyy-MM-dd");
    return String(val);
  }, z.string({ required_error: "End date is required" })),
  start_time: z.string().min(1, "Start time is required"),
  end_time: z.string().min(1, "End time is required"),
  table_id: z.number().min(1, "Table is required"),
  guest: z.coerce.number().min(1, "Guest is required"),
  advance: z.coerce.number().optional(),
  note: z.string().optional(),
  status: z.string().optional(),
});

const AddNewBookingFormSidebar = ({ isOpen, onClose }) => {
  
  const { selectedRoom, tables } = useSelector((state) => state.table.table);
  const { staffList } = useSelector(
      (state) => state.staffManage.attendance
    );
  const [loading, setLoading] = useState(false);
  const [showIsChild, setShowIsChild] = useState(false);
  const { user } = useAuth();
  const [staff, setStaff] = useState([])
  const dispatch = useDispatch();  
// console.log(staff);

  const form = useForm({
    resolver: zodResolver(addNewFloorCardSchema),
    mode: "onChange",
    defaultValues: {
      customer_phone: "",
      customer_name: "",
      customer_email: "",
      reservation_type: "",
      staff_id: "",
      // issue_date: "",
      reservation_date: format(new Date(), "yyyy-MM-dd"),
      start_time: "",
      end_time: "",
      table_id: "",
      guest: "",
      advance: "",
      note: "",
      status: "",
    },
  });

  useEffect(() => {
    if (showIsChild && staffList.length === 0) {
      dispatch(fetchAllStaff());
    }
  }, [showIsChild])

  const { reset } = form;

  const addFloorCardData = async (data) => {
    setLoading(true);

    const payload = {
      ...data,
      restaurant_id: user?.restaurant_id,
      staff_id: showIsChild ? data.staff_id : null,
    };

    try {
      let res;
      res = await createBookingTable(payload);
      if (res.status === "success") {
        toast.success("Booked table successfully!");
        onClose()
        reset();
        // IMPORTANT: refresh tables for selected room to reflect changes immediately
        dispatch(fetchAllTables(selectedRoom.id));
      } else {
        toast.error(res.message || "Something went wrong!");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to process Floor Card");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      form.reset({
        ...form.defaultValues,
        table_id: "",
      });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
        <Card className="bg-cblack4 text-cwhite w-[600px] h-[95vh] overflow-scroll">
          <CardHeader className="flex flex-row justify-between items-center">
            <CardTitle className="text-cwhite text-sm font-medium">New Table Booking </CardTitle>
            <X onClick={onClose} className="cursor-pointer" size={16} />
          </CardHeader>
          <Separator />
          <CardContent>
            <div className="mt-8">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(addFloorCardData)} className="flex flex-col gap-6">
                  <FormInputField name="customer_phone" style="bg-cblack3" placeholder={"Write Phone Number"} form={form} type="text" label={"Phone Number"} required={true} />
                  <FormInputField name="customer_name" style="bg-cblack3" placeholder={"Write Customer Name"} form={form} type="text" label={"Customer Name"} required={true} />
                  <FormInputField name="customer_email" style="bg-cblack3" placeholder={"Write Email Address"} form={form} type="text" label={"Email Address"} required={true} />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <FormInputField
                      name="reservation_type"
                      style="bg-cblack3"
                      placeholder={"Reservation Type*"}
                      form={form}
                      type="select"
                      parseAs="string"
                      selectOption={[
                        { label: "In Person", value: "in_person" },
                        { label: "Phone Call", value: "phone" },
                        { label: "Online", value: "online" },
                        { label: "Other", value: "other" },
                      ]}
                      label={"Reservation Type"}
                      required={true}
                    />
                    </div>
                    <div className="space-y-2">
                      <div className="flex gap-2 items-center">
                        <Switch
                          className="data-[state=unchecked]:bg-neutral-500"
                          checked={showIsChild}
                          onCheckedChange={(val) => setShowIsChild(val)}
                        />
                        <Label>Assign Staff</Label>
                      </div>
      
                      {showIsChild && (
                        <FormInputField
                          name="staff_id"
                          style="bg-cblack3"
                          placeholder={"Select Staff"}
                          form={form}
                          type="select"
                          parseAs="string"
                          selectOption={staffList?.map(item => ({
                            label: item?.user?.first_name + " " + item?.user?.last_name,
                            value: item?.user?.id,
                          }))}
                        />
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="col-span-2">
                      <FormInputField name="reservation_date" style="bg-cblack3" placeholder={"Select Date"} form={form} type="date" dateFormat="yyyy-MM-dd" label={"Date"} required={true} />
                    </div>
                    <div className="col-span-1">
                      <FormInputField name="start_time" style="bg-cblack3" placeholder={"Select Start Time"} form={form} type="time" label={"Start Time"} required={true} />
                    </div>
                    <div className="col-span-1">
                      <FormInputField name="end_time" style="bg-cblack3" placeholder={"Select End Time"} form={form} type="time" label={"End Time"} />
                    </div>
                  </div>
                  <div className="flex gap-4">
                    {/* <div>
                        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Table Number</label>
                        <input
                            className="flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                            // value={floorName}
                            readOnly
                        />
                    </div> */}
                    <FormInputField
                      name="table_id"
                      style="bg-cblack3"
                      placeholder={"Select Table"}
                      form={form}
                      type="select"
                      parseAs="string"
                      selectOption={tables?.map((item) => {
                          return { label: item.name, value: item.id };
                      })}
                      label={"Table Number"}
                      required={true}
                    />
                    <FormInputField name="guest" style="bg-cblack3" placeholder={"Enter Number of Guest"} form={form} type="number" label={"Number Of Guest"} required={true} />
                  </div>
                  <FormInputField name="advance" style="bg-cblack3" placeholder={"Enter Advance Amount"} form={form} type="number" label={"Advance Amount"} />
                  <FormInputField name="note" style="bg-cblack3" placeholder={"Enter Notes"} form={form} type="textarea" label={"Notes"} />
                  <FormInputField
                    name="status"
                    style="bg-cblack3"
                    placeholder={"Select Status"}
                    form={form}
                    type="select"
                    parseAs="string"
                    selectOption={ [
                            { label: "Pending", value: "pending" },
                            { label: "Confirmed", value: "confirmed" },
                          ]
                    }
                    label={"Status"}
                  />
                  <div className="mt-4 flex gap-5 w-full">
                    <Button className="w-full bg-cred text-white" onClick={onClose}>
                        Cancel
                    </Button>

                    <Button isLoading={loading} className="w-full bg-cyellow">
                      Confirm
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default AddNewBookingFormSidebar;
